import { useEffect, useRef, type RefObject } from 'react';

/* =============================================================================
 * Dismiss on outside interaction
 * -----------------------------------------------------------------------------
 * Replaces the invisible full-screen backdrop that popovers used to sit on.
 *
 * That pattern looks bulletproof and is not. A `position: fixed` element is
 * only positioned against the viewport while no ancestor establishes a
 * containing block for it — and `backdrop-filter` does, along with `transform`,
 * `filter`, `perspective` and `contain`. The app header carries
 * `lg:backdrop-blur`, so on a desktop the backdrop was being clipped to the
 * header's own box. Every click below it landed on the page instead, and the
 * menu stayed open with nothing to explain why. On a phone, where the blur
 * class does not apply, the same code worked.
 *
 * A document-level listener has no such dependency. It also keeps a phantom
 * button out of the tab order and the accessibility tree.
 * ========================================================================== */

export function useDismiss<T extends HTMLElement>(
  open: boolean,
  onDismiss: () => void,
): RefObject<T> {
  const ref = useRef<T>(null);

  // Held in a ref so an inline arrow function does not resubscribe the
  // listeners on every render.
  const handler = useRef(onDismiss);
  handler.current = onDismiss;

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      const element = ref.current;
      // The ref wraps the trigger *and* the popover. If it covered only the
      // popover, clicking the trigger to close would dismiss here and then be
      // reopened by the button's own handler — a menu that refuses to shut.
      if (element && !element.contains(event.target as Node)) handler.current();
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') handler.current();
    }

    // `pointerdown` rather than `click`: a click only lands on mouseup, so
    // selecting text inside the menu and releasing outside it would dismiss.
    // Capture phase, so a stopPropagation somewhere in between cannot hide the
    // event from us.
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return ref;
}
